import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { SpinnerIcon } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const variants: Record<ButtonVariant, string> = {
  primary: 'hud-cut-sm bg-accent text-accent-ink hover:bg-accent-strong',
  secondary: 'hud-cut-sm border border-line-strong bg-panel-raised text-text hover:border-accent',
  ghost: 'text-info hover:text-text',
  danger: 'hud-cut-sm border border-danger/60 bg-danger/10 text-danger hover:bg-danger/20',
};

/** Button styling, also for links that look like buttons. */
export const buttonClass = (variant: ButtonVariant = 'primary', className = '') =>
  `inline-flex min-h-11 items-center justify-center gap-2 px-4 font-display text-sm font-bold uppercase tracking-[0.12em] motion-safe:transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${variants[variant]} ${className}`;

export function Button({
  variant = 'primary',
  pending = false,
  className = '',
  type = 'button',
  disabled,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; pending?: boolean }) {
  return (
    <button
      type={type}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      className={buttonClass(variant, className)}
      {...props}
    >
      {pending ? <SpinnerIcon /> : null}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  className = '',
  type = 'button',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={`relative inline-flex size-11 items-center justify-center text-muted hover:text-text disabled:opacity-45 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
